import { useFormikContext } from 'formik';
import { flatten } from 'lodash';
import { useMemo } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import { DropdownButtonField, ValidatedInput } from '../template-components';
import DropdownButton from '../../basics/DropdownButton';
import {
  assignmentOptions,
  assignmentOptionsMap,
  ASSIGNMENTS,
  developerEquipmentTypes,
  ownerEquipmentTypes,
} from '../../../constants/operations';
import { isAsterisk, multiplePlaceholder } from '../../../utils/bulkEdits';
import { useRenderWithBulkErrors } from '../../../hooks/useRenderWithBulkErrors';
import { getOperationEquipmentType } from '../../../utils/operations';
import { useDataScope, useUserRole } from '../../../utils/hooks';
import { captureEvent } from '../../../utils/analytics';
import { numberAsRoundedDecimal } from '../../../utils/formatters';

// Format extra decimal places on dollarsPerUnit to make per-row-foot costs more precise
const DOLLARS_PRECISION = 7;
const formatDollars = (v) =>
  numberAsRoundedDecimal(v, { digits: DOLLARS_PRECISION, minDigits: 2 });

const getSelectedOptionFromValues = (op) => {
  if (isAsterisk(op.contracted) || isAsterisk(op.rented)) return null;
  return getOperationEquipmentType(op);
};

const CapexEquipmentTypeSection = ({ unitOptions, operationUnitSpecMap }) => {
  // Only one of rented or contracted can be selected. If neither is selected
  // the item is assumed to be purchased.
  const { values, setValues } = useFormikContext();
  const { defaultRenderBulkError } = useRenderWithBulkErrors('operations');
  const scope = useDataScope();
  const { isSuperadmin } = useUserRole();

  const useCostOverride =
    values.dollarsPerUnit != null || values.unitForDollars != null;

  const fieldResetValues = {
    ...values,
    contracted: false,
    rented: false,
    equipmentRentalCostPerDay: null,
    dollarsPerUnit: null,
    unitForDollars: null,
  };

  const purchasedResetValues = {
    ...fieldResetValues,
    // note for now on reset to purchased
    // we don't try to guess at how to populate capex_groups
    equipmentName: [],
  };

  const capexGroupsToListOfNames = (groups) =>
    flatten(
      (groups || []).map((group) => [
        group?.capexes.main?.name,
        group?.capexes.implement?.name,
      ]),
    ).filter((name) => name != null);

  const rentedResetValues = {
    ...fieldResetValues,
    rented: true,
    capexGroups: [],
    equipmentName: values.contracted // Previous value
      ? values.equipmentName
      : capexGroupsToListOfNames(values.capexGroups),
    equipmentRentalCostPerDay: '', // Yup doesn't like null here
  };

  const contractedResetValues = {
    ...fieldResetValues,
    contracted: true,
    unitForDollars: 'acre',
    capexGroups: [],
    equipmentName: values.rented // Previous value
      ? values.equipmentName
      : capexGroupsToListOfNames(values.capexGroups),
  };

  const unitSpecForDollars = operationUnitSpecMap[values.unitForDollars];

  const resetValues = {
    [ownerEquipmentTypes.CONTRACTED]: contractedResetValues,
    [ownerEquipmentTypes.RENTED]: rentedResetValues,
    [ownerEquipmentTypes.DIRECT_OPERATED]: purchasedResetValues,
    [developerEquipmentTypes.CONTRACTED]: contractedResetValues,
    [developerEquipmentTypes.RENTED]: rentedResetValues,
    [developerEquipmentTypes.DIRECT_OPERATED]: purchasedResetValues,
  };

  const setSelectedOptionAndUpdateValues = (option) => {
    setValues(resetValues[option.name] || purchasedResetValues);
  };

  const selectedOption = useMemo(
    () => getSelectedOptionFromValues(values),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.assignment, values.contracted, values.rented],
  );

  const dropdownOptions = useMemo(() => {
    const types =
      values.assignment === ASSIGNMENTS.OWNER
        ? ownerEquipmentTypes
        : developerEquipmentTypes;
    return Object.values(types).map((type) => ({
      key: type,
      name: type,
    }));
  }, [values.assignment]);

  const toggleCostOverride = () => {
    if (useCostOverride) {
      // Toggling off, null out cost override
      setValues({
        ...values,
        dollarsPerUnit: null,
        unitForDollars: null,
      });
    } else {
      // Toggling on, set value for acre dropdown
      setValues({ ...values, unitForDollars: 'acre' });
    }
  };

  return (
    <>
      <div className="d-flex align-items-start">
        <FormGroup>
          <Label>Assignment</Label>
          <DropdownButton
            placeholder={multiplePlaceholder(values.assignment)}
            options={assignmentOptions}
            disabled={!isSuperadmin}
            selectedOption={
              assignmentOptionsMap[values.assignment]?.name || undefined
            }
            setOption={(option) => {
              captureEvent('operation-assignment-updated', {
                scope,
                assignment: option.name,
                count: values.operations?.length || 1,
              });
              setValues({
                ...values,
                assignment: option.value,
              });
            }}
            className="mr-2"
            size="med"
            inModal
          />
        </FormGroup>
        <FormGroup>
          <Label>Operation Type</Label>
          <DropdownButton
            placeholder={
              multiplePlaceholder(values.contracted) ||
              multiplePlaceholder(values.rented)
            }
            selectedOption={selectedOption}
            options={dropdownOptions}
            setOption={setSelectedOptionAndUpdateValues}
            className="mr-2"
            size="med"
            inModal
          />
        </FormGroup>
      </div>
      <div className="d-flex align-items-start">
        {selectedOption && !values.rented && (
          <ValidatedInput
            type="number"
            name="dollarsPerUnit"
            placeholder={multiplePlaceholder(values.dollarsPerUnit)}
            format={formatDollars}
            min={0}
            step="any"
            label="Cost"
            unitPrecision={DOLLARS_PRECISION}
            unitSpec={unitSpecForDollars}
            unit="" // Hide units because they are displayed in the dropdown
            currency
            size="med"
            disabled={!(values.contracted || useCostOverride)}
          >
            {({ renderInput, renderError, renderLabel }) => (
              <FormGroup>
                {values.contracted ? (
                  renderLabel()
                ) : (
                  <Label className="d-flex align-items-center">
                    <Input
                      type="checkbox"
                      className="ml-0 mr-2 my-0"
                      checked={useCostOverride}
                      onChange={toggleCostOverride}
                    />
                    Cost Override
                  </Label>
                )}
                <div className="d-flex">
                  {renderInput()}
                  <DropdownButtonField
                    inModal
                    outline
                    className="ml-2"
                    options={unitOptions}
                    name="unitForDollars"
                    placeholder={multiplePlaceholder(values.unitForDollars)}
                    disabled={!(values.contracted || useCostOverride)}
                  />
                </div>
                {renderError()}
                {defaultRenderBulkError('dollarsPerUnit')}
              </FormGroup>
            )}
          </ValidatedInput>
        )}
        {selectedOption && values.rented && (
          <ValidatedInput
            label="Rental cost"
            type="number"
            name="equipmentRentalCostPerDay"
            placeholder={multiplePlaceholder(values.equipmentRentalCostPerDay)}
            min={0}
            step={0.01}
            currency
            size="med"
          >
            {({ renderInput, renderError, renderLabel }) => (
              <FormGroup>
                {renderLabel()}
                <div className="d-flex align-items-center">
                  {renderInput()}
                  <div className="ml-2">per day</div>
                </div>
                {renderError()}
                {defaultRenderBulkError('equipmentRentalCostPerDay')}
              </FormGroup>
            )}
          </ValidatedInput>
        )}
      </div>
    </>
  );
};

export default CapexEquipmentTypeSection;
